import {CloseIconSvg, applyThemeVariables} from './sheet-shell-shared'

export type PanelPlacement = 'left' | 'right' | 'center'

export type PanelPresenterKind = 'side-sheet' | 'center-modal'

export function presenterKindForPlacement(placement: PanelPlacement): PanelPresenterKind {
  return placement === 'center' ? 'center-modal' : 'side-sheet'
}

export function getViewportHeight(): number {
  return window.visualViewport?.height ?? window.innerHeight
}

export interface PanelPresenterCallbacks {
  onDismissRequested: () => void
  onClosed: () => void
}

export interface PanelPresenter {
  readonly kind: PanelPresenterKind
  readonly tracksViewport: boolean
  mount(parent: ParentNode): void
  readonly iframeContainer: HTMLElement
  open(): void
  close(): void
  showLoading(): void
  showReady(): void
  setBodyHeight(height: number): void
  resetBodyHeight(): void
  syncViewport(): void
  applyPlacement(placement: PanelPlacement): void
  setSpinnerColor(color: string | null): void
  destroy(): void
}

const CloseTransitionFallbackMs = 500

interface CloseTransitionState {
  closeCleanup: (() => void) | null
  closeTimeoutId: number | null
}

function teardownCloseTransition(state: CloseTransitionState, element: HTMLElement): void {
  if (state.closeCleanup) {
    element.removeEventListener('transitionend', state.closeCleanup)
    state.closeCleanup = null
  }
  if (state.closeTimeoutId !== null) {
    window.clearTimeout(state.closeTimeoutId)
    state.closeTimeoutId = null
  }
}

function beginCloseTransition(
  state: CloseTransitionState,
  element: HTMLElement,
  onClosed: () => void,
  fallbackMs: number
): void {
  teardownCloseTransition(state, element)

  let cleaned = false
  const cleanup = (): void => {
    if (cleaned) return
    cleaned = true
    element.removeEventListener('transitionend', cleanup)
    state.closeCleanup = null
    if (state.closeTimeoutId !== null) {
      window.clearTimeout(state.closeTimeoutId)
      state.closeTimeoutId = null
    }
    onClosed()
  }

  state.closeCleanup = cleanup
  element.addEventListener('transitionend', cleanup)
  state.closeTimeoutId = window.setTimeout(cleanup, fallbackMs)
}

export class SideSheetPanel implements PanelPresenter {
  readonly kind = 'side-sheet' as const
  readonly tracksViewport = false

  private readonly fontFamily: string
  private readonly mobileDrawer: boolean
  private readonly overlayDismiss: boolean
  private readonly callbacks: PanelPresenterCallbacks
  private placement: PanelPlacement
  private spinnerColor: string | null
  private escapeListener: ((event: KeyboardEvent) => void) | null = null

  private backdrop: HTMLDivElement | null = null

  private sheet!: HTMLDivElement
  private closeButton!: HTMLButtonElement
  private loading!: HTMLDivElement
  private loadingSpinner!: HTMLDivElement
  private iframeContainerEl!: HTMLDivElement
  private closeTransition: CloseTransitionState = {closeCleanup: null, closeTimeoutId: null}

  constructor(options: {
    fontFamily: string
    placement: PanelPlacement
    mobileDrawer: boolean
    overlayDismiss?: boolean
    spinnerColor?: string | null
    callbacks: PanelPresenterCallbacks
  }) {
    this.fontFamily = options.fontFamily
    this.placement = options.placement
    this.mobileDrawer = options.mobileDrawer
    this.overlayDismiss = options.overlayDismiss ?? false
    this.spinnerColor = options.spinnerColor ?? null
    this.callbacks = options.callbacks
  }

  get iframeContainer(): HTMLElement {
    return this.iframeContainerEl
  }

  mount(parent: ParentNode): void {
    if (this.overlayDismiss) {
      this.backdrop = document.createElement('div')
      this.backdrop.className = 'anagram-bits-backdrop'
      this.backdrop.addEventListener('click', () => this.callbacks.onDismissRequested())
      parent.appendChild(this.backdrop)
    }

    this.sheet = document.createElement('div')
    this.sheet.className = this.mobileDrawer ? 'anagram-sheet anagram-ets-sheet' : 'anagram-sheet'
    this.sheet.style.fontFamily = this.fontFamily
    this.applyPlacement(this.placement)

    this.closeButton = document.createElement('button')
    this.closeButton.className = 'anagram-sheet-close'
    this.closeButton.innerHTML = CloseIconSvg
    this.closeButton.setAttribute('aria-label', 'Close')
    this.closeButton.addEventListener('click', () => this.callbacks.onDismissRequested())
    this.sheet.appendChild(this.closeButton)

    this.loading = document.createElement('div')
    this.loading.className = 'anagram-loading'
    this.loadingSpinner = document.createElement('div')
    this.loadingSpinner.className = 'anagram-loading-spinner'
    this.applySpinnerColor()
    this.loading.appendChild(this.loadingSpinner)
    this.sheet.appendChild(this.loading)

    this.iframeContainerEl = document.createElement('div')
    this.iframeContainerEl.className = 'anagram-iframe-container'
    this.sheet.appendChild(this.iframeContainerEl)

    parent.appendChild(this.sheet)
  }

  applyPlacement(placement: PanelPlacement): void {
    this.placement = placement
    if (!this.sheet) {
      return
    }

    const isRightAligned = placement !== 'left'
    this.sheet.style.right = isRightAligned ? '16px' : 'auto'
    this.sheet.style.left = isRightAligned ? 'auto' : '16px'
    this.sheet.style.setProperty(
      '--anagram-sheet-closed-transform',
      isRightAligned ? 'translateX(calc(100% + 32px))' : 'translateX(calc(-100% - 32px))'
    )
  }

  setSpinnerColor(color: string | null): void {
    this.spinnerColor = color
    this.applySpinnerColor()
  }

  private applySpinnerColor(): void {
    if (this.loadingSpinner && this.spinnerColor) {
      this.loadingSpinner.style.borderTopColor = this.spinnerColor
    }
  }

  open(): void {
    teardownCloseTransition(this.closeTransition, this.sheet)
    if (this.backdrop) {
      this.backdrop.classList.add('open')
      this.backdrop.style.opacity = '1'
      this.backdrop.style.pointerEvents = 'auto'
    }

    requestAnimationFrame(() => {
      this.sheet.classList.add('open')
    })
    if (this.overlayDismiss) {
      this.setupEscapeListener()
    }
  }

  close(): void {
    if (this.backdrop) {
      this.backdrop.classList.remove('open')
      this.backdrop.style.opacity = ''
      this.backdrop.style.pointerEvents = ''
    }

    this.sheet.classList.remove('open')
    this.teardownEscapeListener()
    teardownCloseTransition(this.closeTransition, this.sheet)
    beginCloseTransition(this.closeTransition, this.sheet, () => this.callbacks.onClosed(), CloseTransitionFallbackMs)
  }

  private setupEscapeListener(): void {
    if (this.escapeListener) {
      return
    }

    this.escapeListener = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return
      }
      event.preventDefault()
      this.callbacks.onDismissRequested()
    }

    document.addEventListener('keydown', this.escapeListener)
  }

  private teardownEscapeListener(): void {
    if (this.escapeListener) {
      document.removeEventListener('keydown', this.escapeListener)
      this.escapeListener = null
    }
  }

  showLoading(): void {
    this.loading.style.display = 'flex'
    this.iframeContainerEl.classList.remove('ready')
    this.closeButton.style.display = 'flex'
  }

  showReady(): void {
    this.loading.style.display = 'none'
    this.iframeContainerEl.classList.add('ready')
    this.closeButton.style.display = 'none'
  }

  setBodyHeight(): void {}
  resetBodyHeight(): void {}
  syncViewport(): void {}

  destroy(): void {
    teardownCloseTransition(this.closeTransition, this.sheet)
    this.teardownEscapeListener()
    this.backdrop?.remove()
    this.sheet?.remove()
  }
}

const MinDrawerBodyHeightPx = 240
const DrawerViewportTopGapPx = 32
const DesktopDrawerBottomOffsetPx = 84
/**
 * On short desktop viewports the modal drops the room it normally leaves for the
 * launcher bar (which is hidden while the modal is open) and sits evenly inset
 * instead, so the chat gets every pixel it can. Keep in sync with the
 * `max-height` media query in `center-bar-to-drawer.css`.
 */
const ShortDesktopViewportQuery = '(max-height: 640px)'
const ShortDesktopDrawerBottomOffsetPx = DrawerViewportTopGapPx
const MinDrawerBodyViewportRatio = 1 / 2
const BaselineViewportRatio = 0.6

export class CenterModalPanel implements PanelPresenter {
  readonly kind = 'center-modal' as const
  readonly tracksViewport = true

  private readonly fontFamily: string
  private readonly theme: Record<string, string>
  private readonly callbacks: PanelPresenterCallbacks
  private spinnerColor: string | null = null

  private overlayRoot!: HTMLDivElement
  private backdrop!: HTMLDivElement
  private drawer!: HTMLDivElement
  private drawerBody!: HTMLDivElement
  private closeButton!: HTMLButtonElement
  private loading!: HTMLDivElement
  private loadingSpinner!: HTMLDivElement
  private iframeContainerEl!: HTMLDivElement
  private escapeListener: ((event: KeyboardEvent) => void) | null = null
  private closeTransition: CloseTransitionState = {closeCleanup: null, closeTimeoutId: null}
  private lastReportedHeight: number | null = null
  private appliedDrawerBodyHeight: number | null = null
  private isOpen = false

  constructor(options: {
    fontFamily: string
    theme?: Record<string, string>
    spinnerColor?: string | null
    callbacks: PanelPresenterCallbacks
  }) {
    this.fontFamily = options.fontFamily
    this.theme = options.theme ?? {}
    this.spinnerColor = options.spinnerColor ?? null
    this.callbacks = options.callbacks
  }

  get iframeContainer(): HTMLElement {
    return this.iframeContainerEl
  }

  mount(parent: ParentNode): void {
    this.overlayRoot = document.createElement('div')
    this.overlayRoot.className = 'anagram-bits-overlay-root'
    this.overlayRoot.style.fontFamily = this.fontFamily
    this.overlayRoot.style.right = 'auto'
    this.overlayRoot.style.bottom = 'auto'
    applyThemeVariables(this.overlayRoot, this.theme)

    this.backdrop = document.createElement('div')
    this.backdrop.className = 'anagram-bits-backdrop'
    this.backdrop.addEventListener('click', () => this.callbacks.onDismissRequested())
    this.overlayRoot.appendChild(this.backdrop)

    this.drawer = document.createElement('div')
    this.drawer.className = 'anagram-bits-drawer'
    this.drawer.style.fontFamily = this.fontFamily
    this.overlayRoot.appendChild(this.drawer)

    this.closeButton = document.createElement('button')
    this.closeButton.className = 'anagram-bits-drawer-close'
    this.closeButton.innerHTML = CloseIconSvg
    this.closeButton.setAttribute('aria-label', 'Close')
    this.closeButton.addEventListener('click', () => this.callbacks.onDismissRequested())
    this.drawer.appendChild(this.closeButton)

    this.drawerBody = document.createElement('div')
    this.drawerBody.className = 'anagram-bits-drawer-body'

    this.loading = document.createElement('div')
    this.loading.className = 'anagram-loading'
    this.loadingSpinner = document.createElement('div')
    this.loadingSpinner.className = 'anagram-loading-spinner'
    this.applySpinnerColor()
    this.loading.appendChild(this.loadingSpinner)
    this.drawerBody.appendChild(this.loading)

    this.iframeContainerEl = document.createElement('div')
    this.iframeContainerEl.className = 'anagram-iframe-container'
    this.drawerBody.appendChild(this.iframeContainerEl)

    this.drawer.appendChild(this.drawerBody)
    parent.appendChild(this.overlayRoot)

    this.syncViewport()
  }

  applyPlacement(): void {}

  setSpinnerColor(color: string | null): void {
    this.spinnerColor = color
    this.applySpinnerColor()
  }

  private applySpinnerColor(): void {
    if (this.loadingSpinner && this.spinnerColor) {
      this.loadingSpinner.style.borderTopColor = this.spinnerColor
    }
  }

  open(): void {
    teardownCloseTransition(this.closeTransition, this.drawer)
    // Position the frame before flipping `isOpen` so the entrance transform is
    // computed against the correct viewport without pre-setting it (which would
    // start the slide from the wrong place).
    this.syncFrame()
    this.isOpen = true
    requestAnimationFrame(() => {
      // Drop any keyboard-tracking timing from a previous session so the
      // entrance uses its own (longer) transition.
      this.drawer.classList.remove('keyboard-tracking')
      this.backdrop.classList.add('open')
      this.drawer.classList.add('open')
      this.backdrop.style.opacity = '1'
      this.backdrop.style.pointerEvents = 'auto'
      this.drawer.style.opacity = '1'
      this.drawer.style.pointerEvents = 'auto'
      this.drawer.style.transform = this.getOpenTransform()
    })
    this.setupEscapeListener()
  }

  close(): void {
    this.isOpen = false
    this.drawer.classList.remove('keyboard-tracking')
    this.backdrop.classList.remove('open')
    this.drawer.classList.remove('open')
    this.backdrop.style.opacity = ''
    this.backdrop.style.pointerEvents = ''
    this.drawer.style.opacity = ''
    this.drawer.style.pointerEvents = ''
    this.drawer.style.transform = this.getClosedTransform()
    teardownCloseTransition(this.closeTransition, this.drawer)
    this.teardownEscapeListener()
    beginCloseTransition(this.closeTransition, this.drawer, () => this.callbacks.onClosed(), CloseTransitionFallbackMs)
  }

  showLoading(): void {
    this.loading.style.display = 'flex'
    this.iframeContainerEl.classList.remove('ready')
    this.closeButton.style.display = 'flex'
  }

  showReady(): void {
    this.loading.style.display = 'none'
    this.iframeContainerEl.classList.add('ready')
    this.closeButton.style.display = 'none'
  }

  setBodyHeight(height: number): void {
    // The mobile sheet is a fixed 80% of the viewport (governed by CSS) and is
    // intentionally immune to content-driven resizing. Only the desktop modal
    // grows to fit its content.
    if (!this.isDesktopViewport()) {
      return
    }

    const viewportHeight = getViewportHeight()
    const minimumHeight = this.getBaselineDrawerBodyHeight(true, viewportHeight)
    const maximumHeight = this.getMaxDrawerBodyHeight(true, viewportHeight)
    const requestedHeight = Math.min(Math.max(Math.round(height), minimumHeight), maximumHeight)
    const currentHeight = this.appliedDrawerBodyHeight ?? minimumHeight
    this.lastReportedHeight = height
    const nextHeight = currentHeight > maximumHeight ? maximumHeight : Math.max(currentHeight, requestedHeight)
    this.appliedDrawerBodyHeight = nextHeight
    this.drawerBody.style.height = `${nextHeight}px`
  }

  resetBodyHeight(): void {
    if (!this.isDesktopViewport()) {
      return
    }

    this.lastReportedHeight = null
    this.syncDrawerBodyHeight(true)
  }

  syncViewport(): void {
    this.syncFrame()

    if (this.isDesktopViewport()) {
      this.syncDrawerBodyHeight(true, getViewportHeight())
      return
    }

    // Mobile keeps a fixed height; when the keyboard is up we only translate the
    // open sheet so it rides above the keyboard instead of resizing/janking. The
    // keyboard-tracking class swaps in a snappier transition for this movement.
    if (this.isOpen) {
      this.drawer.classList.add('keyboard-tracking')
      this.drawer.style.transform = this.getOpenTransform()
    }
  }

  destroy(): void {
    teardownCloseTransition(this.closeTransition, this.drawer)
    this.teardownEscapeListener()
    this.overlayRoot?.remove()
  }

  private setupEscapeListener(): void {
    if (this.escapeListener) {
      return
    }

    this.escapeListener = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return
      }
      event.preventDefault()
      this.callbacks.onDismissRequested()
    }

    document.addEventListener('keydown', this.escapeListener)
  }

  private teardownEscapeListener(): void {
    if (this.escapeListener) {
      document.removeEventListener('keydown', this.escapeListener)
      this.escapeListener = null
    }
  }

  private isDesktopViewport(): boolean {
    return window.matchMedia('(min-width: 640px)').matches
  }

  private getOpenTransform(): string {
    if (this.isDesktopViewport()) {
      return 'translateX(-50%) translateY(0) scale(1)'
    }

    const keyboardOffset = this.getKeyboardOffset()
    return keyboardOffset > 0 ? `translateY(-${keyboardOffset}px)` : 'translateY(0)'
  }

  private getClosedTransform(): string {
    return this.isDesktopViewport() ? 'translateX(-50%) translateY(18px) scale(0.98)' : 'translateY(calc(100% + 24px))'
  }

  /**
   * How far the on-screen keyboard intrudes into the layout viewport. On iOS the
   * layout viewport (`window.innerHeight`) stays put when the keyboard opens
   * while the visual viewport shrinks, so the difference is the keyboard inset.
   */
  private getKeyboardOffset(): number {
    const viewport = window.visualViewport
    if (!viewport) {
      return 0
    }

    return Math.max(0, Math.round(window.innerHeight - viewport.height - viewport.offsetTop))
  }

  private syncFrame(): void {
    if (this.isDesktopViewport()) {
      const viewport = window.visualViewport
      this.overlayRoot.style.top = `${viewport?.offsetTop ?? 0}px`
      this.overlayRoot.style.left = `${viewport?.offsetLeft ?? 0}px`
      this.overlayRoot.style.width = `${viewport?.width ?? window.innerWidth}px`
      this.overlayRoot.style.height = `${getViewportHeight()}px`
      return
    }

    // Mobile anchors to the layout viewport so the sheet's fixed height stays
    // stable; keyboard avoidance is handled with a transform, not a resize.
    this.overlayRoot.style.top = '0px'
    this.overlayRoot.style.left = '0px'
    this.overlayRoot.style.width = `${window.innerWidth}px`
    this.overlayRoot.style.height = `${window.innerHeight}px`
  }

  private getDesktopBottomOffset(): number {
    return window.matchMedia(ShortDesktopViewportQuery).matches
      ? ShortDesktopDrawerBottomOffsetPx
      : DesktopDrawerBottomOffsetPx
  }

  private getMaxDrawerBodyHeight(isDesktop: boolean, viewportHeight: number): number {
    const bottomOffset = isDesktop ? this.getDesktopBottomOffset() : 0
    return Math.max(MinDrawerBodyHeightPx, viewportHeight - DrawerViewportTopGapPx - bottomOffset)
  }

  private getBaselineDrawerBodyHeight(isDesktop: boolean, viewportHeight: number): number {
    const maximumHeight = this.getMaxDrawerBodyHeight(isDesktop, viewportHeight)
    const minimumHeight = Math.min(
      Math.max(Math.round(viewportHeight * MinDrawerBodyViewportRatio), MinDrawerBodyHeightPx),
      maximumHeight
    )
    const preferredHeight = Math.round(viewportHeight * BaselineViewportRatio)
    return Math.min(Math.max(preferredHeight, minimumHeight), maximumHeight)
  }

  private syncDrawerBodyHeight(isDesktop?: boolean, viewportHeight?: number): void {
    const desktop = isDesktop ?? this.isDesktopViewport()
    const height = viewportHeight ?? getViewportHeight()
    const minimumHeight = this.getBaselineDrawerBodyHeight(desktop, height)
    const maximumHeight = this.getMaxDrawerBodyHeight(desktop, height)
    const currentHeight = this.appliedDrawerBodyHeight ?? minimumHeight
    const clampedCurrentHeight = Math.min(Math.max(currentHeight, minimumHeight), maximumHeight)
    const desiredHeight =
      this.lastReportedHeight == null
        ? clampedCurrentHeight
        : Math.min(Math.max(Math.round(this.lastReportedHeight), minimumHeight), maximumHeight)
    const nextHeight = Math.max(clampedCurrentHeight, desiredHeight)
    this.appliedDrawerBodyHeight = nextHeight
    this.drawerBody.style.height = `${nextHeight}px`
  }
}
